import { StaticJsonRpcProvider } from '@ethersproject/providers'
import { fetchJson } from '@ethersproject/web'

const TRANSIENT_RETRIES = 3

// 429 rate limiting plus the transient 5xx family the Lumia RPC gateway produces under load
// (HTTP 503 "Service Unavailable" and JSON-RPC bodies saying "server unavailable").
const isTransientRpcError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error)
  return /\b429\b|\b50[234]\b|too many requests|rate limit|server unavailable|service unavailable|bad gateway/i.test(
    message,
  )
}

/** Retries `request` with exponential backoff while it fails with a transient RPC error. */
export const withTransientRpcRetry = async <T>(request: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await request()
    } catch (error) {
      if (attempt >= TRANSIENT_RETRIES || !isTransientRpcError(error)) {
        throw error
      }
      await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt + Math.random() * 250))
    }
  }
}

/**
 * Minimum spacing between outgoing HTTP dispatches, shared by every read-only provider in the
 * app. The public Lumia RPC gateway rate-limits by requests per second, and uncoordinated
 * subsystems (safe info, SDK init, history scan, balances) each pacing themselves is not enough:
 * only a global budget keeps the combined load under the limit.
 */
const RPC_DISPATCH_INTERVAL_MS = 250

let nextDispatchAt = 0
let pendingDispatches = 0
let lastDispatchAt = 0

export const acquireRpcDispatchSlot = async (): Promise<void> => {
  pendingDispatches++
  const now = Date.now()
  const wait = Math.max(0, nextDispatchAt - now)
  nextDispatchAt = Math.max(now, nextDispatchAt) + RPC_DISPATCH_INTERVAL_MS
  if (wait > 0) {
    await new Promise((resolve) => setTimeout(resolve, wait))
  }
  pendingDispatches--
  lastDispatchAt = Date.now()
}

const QUIET_CHECK_INTERVAL_MS = 150
const QUIET_THRESHOLD_MS = 400
const QUIET_MAX_WAIT_MS = 10_000

/**
 * Resolves once the shared dispatch queue has been idle for a moment (or after a bounded wait, so
 * a busy page cannot starve the caller forever). Background work such as the multi-chunk history
 * scan yields between requests through this, letting interactive calls (safe info, transaction
 * details) jump ahead instead of queueing behind ~22 scan chunks for many seconds.
 */
export const waitForRpcQuietPeriod = async (): Promise<void> => {
  const started = Date.now()
  while (Date.now() - started < QUIET_MAX_WAIT_MS) {
    if (pendingDispatches === 0 && Date.now() - lastDispatchAt >= QUIET_THRESHOLD_MS) {
      return
    }
    await new Promise((resolve) => setTimeout(resolve, QUIET_CHECK_INTERVAL_MS))
  }
}

const BATCH_WINDOW_MS = 10
const BATCH_LIMIT = 50

type JsonRpcRequest = { method: string; params: Array<unknown>; id: number; jsonrpc: '2.0' }
type JsonRpcResult = { id: number; result?: unknown; error?: { message: string; code?: number; data?: unknown } }
type PendingCall = {
  request: JsonRpcRequest
  resolve: (value: unknown) => void
  reject: (reason: Error) => void
}

/**
 * Batching JSON-RPC provider with global dispatch throttling and transient-error retries.
 *
 * Calls made within the same 10ms window go out as one JSON-RPC batch (capped at BATCH_LIMIT),
 * every HTTP dispatch waits for the shared rate-limit slot, and a 429/5xx response retries the
 * whole batch with backoff before failing. Errors are shaped exactly like ethers' own
 * JsonRpcBatchProvider rejections (message, code, data), so downstream revert handling works
 * unchanged. ethers' stock batch provider offers no hook between queueing and dispatch, which is
 * why the queue is reimplemented here.
 *
 * Extends StaticJsonRpcProvider: the chain never changes for a given RPC URL here, and the
 * static base detects the network once instead of re-polling eth_chainId, which matters a lot
 * behind the shared dispatch budget.
 */
export class ThrottledBatchProvider extends StaticJsonRpcProvider {
  _batchQueue: PendingCall[] = []
  _batchTimer: ReturnType<typeof setTimeout> | null = null

  send(method: string, params: Array<unknown>): Promise<unknown> {
    const request: JsonRpcRequest = { method, params, id: this._nextId++, jsonrpc: '2.0' }

    return new Promise((resolve, reject) => {
      this._batchQueue.push({ request, resolve, reject })

      if (!this._batchTimer) {
        this._batchTimer = setTimeout(() => this._flushBatch(), BATCH_WINDOW_MS)
      }
    })
  }

  async _flushBatch(): Promise<void> {
    this._batchTimer = null
    const batch = this._batchQueue.splice(0, BATCH_LIMIT)

    if (this._batchQueue.length > 0) {
      this._batchTimer = setTimeout(() => this._flushBatch(), BATCH_WINDOW_MS)
    }
    if (batch.length === 0) {
      return
    }

    const payload = JSON.stringify(batch.map((call) => call.request))

    let results: JsonRpcResult[]
    try {
      const response = await withTransientRpcRetry(async () => {
        await acquireRpcDispatchSlot()
        return fetchJson(this.connection, payload)
      })
      results = Array.isArray(response) ? response : [response]
    } catch (error) {
      batch.forEach((call) => call.reject(error as Error))
      return
    }

    const byId = new Map(results.map((result) => [result.id, result]))

    for (const call of batch) {
      const result = byId.get(call.request.id)

      if (!result) {
        call.reject(new Error(`missing response for request ${call.request.id} (method=${call.request.method})`))
      } else if (result.error) {
        const error = new Error(result.error.message)
        ;(error as Error & { code?: number; data?: unknown }).code = result.error.code
        ;(error as Error & { code?: number; data?: unknown }).data = result.error.data
        call.reject(error)
      } else {
        call.resolve(result.result)
      }
    }
  }
}
